/**
 * MODULE: domain/pages/frontend/components/editor/PagesCrmDealPreviewBlockView.tsx
 * PURPOSE: Live CRM deal snapshot card block (Sprint 9)
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineBriefcase } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useCrmEntityPreview } from '../../hooks/useCrmEntityPreview';
import { crmMentionPath } from '../../utils/crmMentionUtils';
import { formatDealAmount, formatDealStageLabel } from '../../utils/pageDealRoomUtils';
import type { PageDealSummary } from '../../../contracts/pages.types';
import { CrmPreviewBlockShell } from './CrmPreviewBlockShell';

export function PagesCrmDealPreviewBlockView({ node, selected }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const dealId = String(node.attrs.dealId ?? '');
  const fallbackLabel = String(node.attrs.dealLabel ?? 'Untitled deal').trim() || 'Untitled deal';
  const { data: rawData, isLoading, isError, refetch, isFetching } = useCrmEntityPreview(
    'deal',
    dealId || null,
    { enabled: !isPublicView }
  );
  const data = rawData as PageDealSummary | undefined;

  const dealName = isPublicView ? fallbackLabel : (data?.name ?? fallbackLabel);
  const stage = !isPublicView && data?.stage ? formatDealStageLabel(data.stage) : null;
  const amount = !isPublicView ? formatDealAmount(data?.amount ?? null) : null;
  const account = !isPublicView ? data?.account_name : null;
  const metaLine = isPublicView
    ? 'Saved deal reference from the shared page'
    : [stage, amount, account].filter(Boolean).join(' · ') || 'Open in CRM for full details';

  return (
    <NodeViewWrapper className="my-3">
      <CrmPreviewBlockShell
        testIdPrefix="pages-crm-deal-preview"
        selected={selected}
        ringClass="ring-orange-400"
        borderClass="border-orange-200 dark:border-orange-900/50"
        gradientClass="from-orange-50 to-white dark:from-orange-950/30 dark:to-gray-900"
        icon={
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200">
            <HiOutlineBriefcase className="h-5 w-5" aria-hidden />
          </span>
        }
        badgeLabel="CRM deal"
        badgeClassName="bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200"
        title={dealName}
        meta={metaLine}
        isLoading={!isPublicView && isLoading}
        isError={!isPublicView && isError}
        errorHint="Could not load live deal data. Showing saved label."
        isFetching={!isPublicView && isFetching}
        entityId={dealId}
        onRefresh={() => void refetch()}
        onOpenCrm={() => dealId && navigate(crmMentionPath('deal', dealId))}
        publicReadOnly={isPublicView}
      />
    </NodeViewWrapper>
  );
}
